<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { newColors } from '../../../colors';
import { useMediaQuery } from '@vueuse/core';
import { useDraftImageStore } from '@/dashboard/store/draftImageStore';

const draftImageStore = useDraftImageStore();
const { activePoint } = storeToRefs(draftImageStore);

const colors = ref(newColors);
const colorPicker = ref<HTMLDivElement | null>(null);
const messenger = ref(document.querySelector('.messenger'));
const isSmartphone = useMediaQuery('(max-width: 1093px)');

async function changeColor(colorNumber: number) {
  if (activePoint.value?.id) {
    await draftImageStore.changeColor({ colorNumber, pointId: activePoint.value.id });
  }
}

const styles = computed(() => {
  if (isSmartphone.value) {
    let styles = {};
    let colorPickerBounds = colorPicker.value?.getBoundingClientRect();
    let messengerBounds = messenger.value?.getBoundingClientRect();
    if (!!colorPickerBounds && !!messengerBounds && colorPickerBounds.height > messengerBounds.height) {
      styles = {
        top: messengerBounds.height - colorPickerBounds.height,
      };
    }
    return styles;
  }
  return {};
});
</script>

<template>
  <div
    ref="colorPicker"
    :style="styles"
    class="color-picker right-[-92px] w-[100px] rounded-8 bg-[#F2F2F280] max-tablet:right-[56px] max-tablet:top-[10px] max-tablet:w-[184px] max-tablet:rounded-[10px] max-tablet:bg-gray-100 max-tablet:shadow-s-menu dark:max-tablet:bg-gray-800"
  >
    <div class="color-picker__header">
      <button
        class="tppr-button__animation w-[84px] rounded-4 p-[4px] hover:bg-gray-900/10 max-tablet:w-full max-tablet:rounded-8 max-tablet:p-xs [&.active]:bg-[#28282826] max-tablet:[&.active]:bg-gray-250 dark:max-tablet:[&.active]:bg-gray-700"
        :class="{ active: activePoint.color === 10 }"
        @click="changeColor(10)"
      >
        <i class="application-icon-inversion flex h-[20px] w-[20px] max-tablet:h-[40px] max-tablet:w-[40px]"></i>
        <span class="ml-[6px] text-[11px] text-gray-900 max-tablet:ml-s max-tablet:text-[17px] max-tablet:font-medium max-tablet:dark:text-white"
          >Inversion</span
        >
      </button>
    </div>

    <div class="color-picker__divider max-tablet:hidden"></div>

    <div class="color-picker__colors">
      <button
        v-for="(color, index) in colors"
        :key="color"
        class="color tppr-button__animation h-[28px] w-[28px] rounded-4 hover:bg-gray-900/10 max-tablet:h-[56px] max-tablet:w-[56px] max-tablet:rounded-8 [&.active]:bg-[#28282826] max-tablet:[&.active]:bg-gray-250 dark:max-tablet:[&.active]:bg-gray-700"
        :class="{ active: activePoint.color === index + 11 }"
        @click="changeColor(index + 11)"
      >
        <span
          class="color__inner h-[20px] w-[20px] rounded-2 max-tablet:h-[40px] max-tablet:w-[40px] max-tablet:rounded-4"
          :style="{ 'background-color': color }"
        ></span>
      </button>
    </div>

    <div class="hidden p-xs max-tablet:flex">
      <button class="tppr-button h-[44px] !bg-black">
        <span class="tppr-button__text">Close</span>
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
$inter-font: 'Inter', sans-serif;

.color-picker {
  z-index: 1;
  backdrop-filter: blur(10px);
  position: absolute;
  padding: 8px;

  &:before {
    content: '';
    position: absolute;
    left: -6px;
    top: calc(50% - 5px);
    border-top: solid transparent 5px;
    border-bottom: solid transparent 5px;
    border-right: solid rgba(242, 242, 242, 0.5) 6px;
    backdrop-filter: blur(10px);
  }

  &__divider {
    height: 1px;
    margin: 4px 0;
    background: rgba(124, 124, 124, 0.3);
  }

  &__header {
    button {
      display: flex;
      align-items: center;

      span {
        font-family: $inter-font;
        font-style: normal;
      }
    }
  }

  &__colors {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;

    .color {
      display: flex;
      justify-content: center;
      align-items: center;

      &__inner {
        display: flex;
      }
    }
  }
}

@media screen and (max-width: 640px) {
  .color-picker {
    &:before {
      display: none;
      //left: auto;
      //right: -11px;
      //border-right: solid transparent 5px;
      //border-left: solid rgba(242, 242, 242, 0.50) 6px;
    }
  }
}
</style>
